import { zeroAddress } from 'viem'
import { gearboxV3LenderKey } from '@1delta/data-sdk'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { BPS, parseRawAmount } from '../../../utils/parsing'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'

// Gearbox has no native-ETH path — pools and credit accounts only hold ERC-20
// WETH. Keep underlyings as the wrapped token; `zeroAddress` stays reserved
// for protocols that genuinely expose a native-asset market.

/**
 * Gearbox rates are in RAY (1e27). To get percent:
 *   rate / 1e27 * 100  =  rate / 1e25
 */
const RAY_TO_PERCENT = 1e25
const scaleGearboxRate = (
  raw: string | number | bigint | undefined,
): number => {
  if (raw === undefined || raw === null) return 0
  return Number(raw) / RAY_TO_PERCENT
}

/**
 * Parser for the single-call Gearbox V3.1 public data fetch.
 *
 * data[0] = MarketData[]   (MarketCompressor.getMarkets(filter))
 *
 * MarketCompressor collapses the old `getPoolsV3List + getCreditManagersV3List`
 * pair into one nested struct per market:
 *
 *     MarketData { pool, quotaKeeper, priceOracle, creditManagers[], ... }
 *
 * Each `creditManagers[i]` carries its own `creditFacade` sub-struct
 * (with minDebt / maxDebt / expirationDate / isPaused / degenNFT /
 * forbiddenTokensMask) plus the `creditManager` sub-struct
 * (collateralTokens[{token, liquidationThreshold}], maxEnabledTokens,
 * fees). We fan out to per-CM `GEARBOX_V3_<CM_HEX>` buckets — one CA is
 * bound to exactly one CM, so CMs are the isolation unit.
 *
 * Filtering applied here:
 *   - skip the whole market if the pool is paused
 *   - skip individual CMs that are paused, expired, or in degen-NFT mode
 *     (can't be operated without a DegenNFT, which we don't support)
 *
 * Per-CM totals (totalDebt, availableToBorrow) come from the pool's
 * `creditManagerDebtParams[]` array, indexed by the CM address.
 *
 * The passive ERC-4626 lender side (PoolV3) is NOT modeled here — it's a
 * standalone vault with no borrow and lives under `vaults/gearbox/`.
 */
export const getGearboxV3PublicDataConverter = (
  _lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => { [lender: string]: any } | undefined, number] => {
  const expectedNumberOfCalls = 1

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined

      const [markets] = data
      if (!Array.isArray(markets)) return undefined

      const out: { [lender: string]: any } = {}
      const now = Math.floor(Date.now() / 1000)

      for (const m of markets) {
        const pool = m?.pool
        if (!pool) continue
        if (pool.isPaused) continue

        const poolAddr = (pool.baseParams?.addr ?? '').toString().toLowerCase()
        const rawUnderlying = (pool.underlying ?? '').toString().toLowerCase()
        if (!poolAddr || !rawUnderlying) continue

        const underlying = rawUnderlying
        const underlyingMeta = tokenList[underlying]
        const underlyingDecimals = Number(
          underlyingMeta?.decimals ?? pool.decimals ?? 18,
        )
        const supplyRate = scaleGearboxRate(pool.supplyRate)
        const underlyingPriceKey =
          toOracleKey(underlyingMeta?.assetGroup) ??
          toGenericPriceKey(underlying, chainId)
        const underlyingPrice = prices[underlyingPriceKey] ?? 0

        // Index per-CM debt params by CM address for O(1) join below.
        const debtByCm: Record<
          string,
          { borrowed: bigint; limit: bigint; available: bigint }
        > = {}
        for (const dp of pool.creditManagerDebtParams ?? []) {
          const addr = (dp?.creditManager ?? '').toString().toLowerCase()
          if (!addr) continue
          debtByCm[addr] = {
            borrowed: BigInt(dp.borrowed ?? 0),
            limit: BigInt(dp.limit ?? 0),
            available: BigInt(dp.available ?? 0),
          }
        }

        // Pool-level utilization drives the IRM. LinearInterestRateModelV3
        // receives (expectedLiquidity, availableLiquidity) and computes
        //   u = (expected − available) / expected
        // where (expected − available) = totalBorrowed + base interest
        // accrued since the last pool update — bare totalBorrowed slightly
        // understates the IRM input.
        const poolExpectedLiquidity = Number(
          parseRawAmount(
            pool.expectedLiquidity?.toString(),
            underlyingDecimals,
          ),
        )
        const poolAvailableLiquidity = Number(
          parseRawAmount(
            pool.availableLiquidity?.toString(),
            underlyingDecimals,
          ),
        )
        const poolIrmDebt = Math.max(
          poolExpectedLiquidity - poolAvailableLiquidity,
          0,
        )
        const poolUtilization =
          poolExpectedLiquidity > 0 ? poolIrmDebt / poolExpectedLiquidity : 0

        // Per-collateral QUOTA rate (pool-level, shared across this market's
        // credit managers). Gearbox charges quota interest on the quoted amount
        // of each collateral token on TOP of the pool base rate — the Gearbox
        // UI shows it as "Quota Rate for <token>". The keeper's uint16 `rate` is
        // bps of 100% APR (10000 = 100%) → percent-as-number to match
        // depositRate / variableBorrowRate (`rate / 100`). Only active,
        // non-zero quotas carry a cost. `rateKeeper.rates[]` is the fallback
        // source when the keeper doesn't surface `quotas[]`.
        const quotaRatePctByToken: Record<string, number> = {}
        const quotaSrc =
          (Array.isArray(m?.quotaKeeper?.quotas) && m.quotaKeeper.quotas) ||
          (Array.isArray(m?.rateKeeper?.rates) && m.rateKeeper.rates) ||
          []
        for (const q of quotaSrc) {
          const t = (q?.token ?? '').toString().toLowerCase()
          if (!t) continue
          if (q?.isActive === false) continue
          const rate = Number(q?.rate ?? 0)
          if (!(rate > 0)) continue
          quotaRatePctByToken[t] = rate / 100
        }

        for (const cm of m.creditManagers ?? []) {
          const facade = cm?.creditFacade
          const manager = cm?.creditManager
          if (!facade || !manager) continue

          // Filter expired / paused / degen-gated CMs.
          const expDate = Number(facade.expirationDate ?? 0)
          const isExpired = !!facade.expirable && expDate > 0 && expDate <= now
          if (facade.isPaused || isExpired) continue
          const degenNFT = (facade.degenNFT ?? zeroAddress)
            .toString()
            .toLowerCase()
          if (degenNFT !== zeroAddress.toLowerCase()) continue

          const cmAddr = (manager.baseParams?.addr ?? '')
            .toString()
            .toLowerCase()
          const facadeAddr = (facade.baseParams?.addr ?? '')
            .toString()
            .toLowerCase()
          if (!cmAddr || !facadeAddr) continue

          let lender: string
          try {
            lender = gearboxV3LenderKey(cmAddr)
          } catch {
            continue
          }

          const cmDebt = debtByCm[cmAddr] ?? {
            borrowed: 0n,
            limit: 0n,
            available: 0n,
          }
          const availableToBorrow = Number(
            parseRawAmount(cmDebt.available.toString(), underlyingDecimals),
          )
          const totalDebtCm = Number(
            parseRawAmount(cmDebt.borrowed.toString(), underlyingDecimals),
          )
          // The CM doesn't hold deposits — the pool does. This CM's share
          // of pool deposits equals what it has currently drawn
          // (totalDebt) plus what it can still borrow (availableToBorrow).
          const totalDepositsCm = availableToBorrow + totalDebtCm

          // V3.1 facade carries minDebt/maxDebt and per-block debt cap
          // (`maxDebt * maxDebtPerBlockMultiplier`). `baseBorrowRate`
          // moved to the pool level — use pool's value.
          const borrowRate = scaleGearboxRate(pool.baseInterestRate)

          // Liquidation penalty fraction for this CM. V3.1 surfaces the
          // `liquidationDiscount` (BPS, 10000 = 100%) flat on the
          // creditManager sub-struct — the liquidator receives the
          // underlying at this discount, so the penalty (premium) is
          // `(10000 - liquidationDiscount) / 10000`. A zero/undefined
          // discount (shouldn't happen on a live CM) falls back to the
          // common Gearbox V3 default of ~4%.
          const liquidationDiscount = Number(manager.liquidationDiscount ?? 0)
          const liquidationPenalty =
            liquidationDiscount > 0 ? (BPS - liquidationDiscount) / BPS : 0.04

          const data: any = {}

          // ── LOAN / borrow entry (pool underlying) ────────────────
          const loanMarketUid = createMarketUid(chainId, lender, underlying)
          data[loanMarketUid] = {
            marketUid: loanMarketUid,
            name: 'Loan ' + (underlyingMeta?.symbol ?? pool.symbol ?? ''),
            poolId: underlying,
            underlying,
            asset: underlyingMeta,
            totalDeposits: totalDepositsCm,
            totalDebtStable: 0,
            totalDebt: totalDebtCm,
            totalLiquidity: availableToBorrow,
            borrowLiquidity: availableToBorrow,
            totalDepositsUSD: totalDepositsCm * underlyingPrice,
            totalDebtStableUSD: 0,
            totalDebtUSD: totalDebtCm * underlyingPrice,
            totalLiquidityUSD: availableToBorrow * underlyingPrice,
            borrowLiquidityUSD: availableToBorrow * underlyingPrice,
            utilization: poolUtilization,
            // Pool-level totals — the basis the IRM's utilization is defined
            // over (shared across all credit managers on this pool).
            irmTotalDeposits: poolExpectedLiquidity,
            irmTotalDebt: poolIrmDebt,
            depositRate: supplyRate,
            variableBorrowRate: borrowRate,
            stableBorrowRate: 0,
            intrinsicYield:
              additionalYields?.intrinsicYields?.[underlyingPriceKey] ?? 0,
            rewards: [],
            decimals: underlyingDecimals,
            config: {
              '0': {
                category: 0,
                borrowCollateralFactor: 0,
                collateralFactor: 0,
                borrowFactor: 1,
                collateralDisabled: true,
                debtDisabled: false,
                liquidationPenalty,
                closeFactor: 1,
              },
            },
            liquidationBonus: liquidationPenalty,
            closeFactor: 1,
            collateralActive: false,
            borrowingEnabled: true,
            depositsEnabled: false,
            hasStable: false,
            isActive: true,
            isFrozen: false,
          }

          // ── COLLATERAL entries ───────────────────────────────────
          // V3.1: array of {token, liquidationThreshold} tuples — no
          // more parallel arrays to index into.
          const collateralTokens: any[] = Array.isArray(
            manager.collateralTokens,
          )
            ? manager.collateralTokens
            : []

          for (const ct of collateralTokens) {
            const rawToken = (ct?.token ?? '').toString().toLowerCase()
            if (!rawToken) continue
            const token = rawToken
            const ltRaw = Number(ct?.liquidationThreshold ?? 0)
            // Gearbox LT is 1e4 precision (e.g. 9000 = 90%).
            const lt = ltRaw / BPS
            if (lt <= 0) continue

            const ctMeta = tokenList[token]
            const ctDecimals = Number(ctMeta?.decimals ?? 18)
            const ctPriceKey =
              toOracleKey(ctMeta?.assetGroup) ??
              toGenericPriceKey(token, chainId)

            const colMarketUid = createMarketUid(chainId, lender, token)
            // Skip if the collateral token duplicates the loan entry
            // (the pool's own underlying may appear in collateralTokens
            // for self-collateralized markets). The loan entry already
            // covers it.
            if (colMarketUid === loanMarketUid) continue

            // Gearbox quota carrying cost for this collateral (percent APR).
            // It is a BORROW-side cost, but it is charged per quoted collateral
            // and scales with collateral value, so we net it into the
            // collateral's `intrinsicYield` (the one rate field that survives
            // the public→meta reshape end-to-end). Effect on net position APR is
            // identical to adding it to the debt-side borrow interest, since
            // both divide by the same equity. `quotaRate` is ALSO surfaced
            // explicitly for attribution/borrow-side display — it is already
            // reflected in `intrinsicYield`, so consumers must not subtract it
            // twice.
            const quotaRate = quotaRatePctByToken[token] ?? 0
            const grossIntrinsic =
              additionalYields?.intrinsicYields?.[ctPriceKey] ?? 0

            data[colMarketUid] = {
              marketUid: colMarketUid,
              name: 'Collateral ' + (ctMeta?.symbol ?? ''),
              poolId: token,
              underlying: token,
              asset: ctMeta,
              totalDeposits: 0,
              totalDebtStable: 0,
              totalDebt: 0,
              totalLiquidity: 0,
              borrowLiquidity: 0,
              totalDepositsUSD: 0,
              totalDebtStableUSD: 0,
              totalDebtUSD: 0,
              totalLiquidityUSD: 0,
              borrowLiquidityUSD: 0,
              utilization: 0,
              depositRate: 0,
              variableBorrowRate: 0,
              stableBorrowRate: 0,
              intrinsicYield: grossIntrinsic - quotaRate,
              quotaRate,
              rewards: [],
              decimals: ctDecimals,
              config: {
                '0': {
                  category: 0,
                  borrowCollateralFactor: lt,
                  collateralFactor: lt,
                  borrowFactor: 1,
                  collateralDisabled: false,
                  debtDisabled: true,
                  liquidationPenalty,
                  closeFactor: 1,
                },
              },
              liquidationBonus: liquidationPenalty,
              closeFactor: 1,
              collateralActive: true,
              borrowingEnabled: false,
              depositsEnabled: false,
              hasStable: false,
              isActive: true,
              isFrozen: false,
            }
          }

          out[lender] = {
            data,
            params: {
              creditManager: {
                lender,
                creditManager: cmAddr,
                creditFacade: facadeAddr,
                pool: poolAddr,
                underlying,
                underlyingDecimals,
                minDebt: facade.minDebt?.toString() ?? '0',
                maxDebt: facade.maxDebt?.toString() ?? '0',
                degenNFT,
                forbiddenTokenMask:
                  facade.forbiddenTokensMask?.toString() ?? '0',
                isDegenMode: false, // filtered above; kept for shape-compat
              },
            },
            chainId,
          }
        }
      }

      return out
    },
    expectedNumberOfCalls,
  ]
}
